import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { onAuth, isAdminEmail } from "../lib/dataStore";
import { normalizeProduct } from "../lib/productHelpers";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuth((u) => { setUser(u); setLoading(false); });
    return () => unsub && unsub();
  }, []);

  const value = {
    user, loading,
    isAdmin: isAdminEmail(user?.email),
    isVerified: !user ? false : (user.emailVerified !== false),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

// ---------- Cart Context ----------
const CartContext = createContext(null);
const CART_KEY = "lc_cart";

const readCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) ?? []; } catch { return []; }
};

const keyOf = (id, variantTag) => `${id}::${variantTag || "Default"}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(items)); }, [items]);

  const add = useCallback((product, variant, qty = 1) => {
    const norm = normalizeProduct(product);
    const selVariant = variant || norm?.variants?.[0];
    const variantTag = selVariant?.tag || "Default";
    const image = selVariant?.images?.[0] || norm?.images?.[0] || "";
    const itemPrice = selVariant?.price != null && selVariant?.price !== "" ? Number(selVariant.price) : Number(norm.price || 0);
    const itemMrp = selVariant?.mrp != null && selVariant?.mrp !== "" ? Number(selVariant.mrp) : Number(norm.mrp || itemPrice);
    const shippingCharge = Number(norm.shippingCharge || 0);

    const k = keyOf(product.id, variantTag);
    setItems((prev) => {
      const found = prev.find((i) => i.key === k);
      if (found) {
        return prev.map((i) => i.key === k ? { ...i, price: itemPrice, mrp: itemMrp, shippingCharge, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        key: k,
        id: product.id,
        variantTag,
        name: product.name,
        price: itemPrice,
        mrp: itemMrp,
        shippingCharge,
        image,
        quantity: qty
      }];
    });
  }, []);

  const remove = useCallback((k) => setItems((p) => p.filter((i) => i.key !== k)), []);
  const setQty = useCallback((k, quantity) => {
    setItems((p) => p.map((i) => i.key === k ? { ...i, quantity: Math.max(1, quantity) } : i));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + (Number(i.price) || 0) * i.quantity, 0);
  const shippingTotal = items.reduce((s, i) => s + (Number(i.shippingCharge) || 0) * i.quantity, 0);
  const total = subtotal + shippingTotal;
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, subtotal, shippingTotal, total, count }}>
      {children}
    </CartContext.Provider>
  );
}
export const useCart = () => useContext(CartContext);
